import { useState } from 'react';
import { Post } from '../App';
import PostList from './PostList';
import { apiConfig } from '../config/api';

interface SearchTabProps {
  userInfo: any;
}

export default function SearchTab({ userInfo }: SearchTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Post[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchType, setSearchType] = useState<'all' | 'content' | 'user'>('all');

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch(`${apiConfig.endpoints.search}?q=${encodeURIComponent(searchQuery)}&type=${searchType}`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('검색 실패:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold mb-4">검색</h2>
      
      {/* 검색 입력 */}
      <div className="mb-4">
        <div className="relative mb-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="게시물 검색..."
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={handleSearch}
            disabled={isSearching}
            className="absolute right-2 top-2 p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            {isSearching ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-gray-600"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </button>
        </div>

        {/* 검색 타입 선택 */}
        <div className="flex space-x-2">
          <button
            onClick={() => setSearchType('all')}
            className={`px-3 py-1 rounded-full text-sm ${
              searchType === 'all' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            전체
          </button>
          <button
            onClick={() => setSearchType('content')}
            className={`px-3 py-1 rounded-full text-sm ${
              searchType === 'content' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            내용
          </button>
          <button
            onClick={() => setSearchType('user')}
            className={`px-3 py-1 rounded-full text-sm ${
              searchType === 'user' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            사용자
          </button>
        </div>
      </div>

      {/* 검색 결과 */}
      {searchResults.length > 0 ? (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            "{searchQuery}"에 대한 검색 결과 {searchResults.length}개
          </p>
          <PostList posts={searchResults} userInfo={userInfo} />
        </div>
      ) : searchQuery && !isSearching ? (
        <div className="text-center text-gray-500 py-8">
          검색 결과가 없습니다.
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p>게시물을 검색해보세요</p>
        </div>
      )}
    </div>
  );
}